import OutputSpecification from "interfaces/howItWorks/OutputSpecification";
import { useCallback, useEffect, useState } from "react";
import ExampleDataViewer from "./HowItWorks/ExampleDataViewer";
import OutputDataRefresher from "./HowItWorks/OutputDataRefresher";
import OutputDataViewer from "./HowItWorks/OutputDataViewer";
import OutputSpecificationSelection from "./HowItWorks/OutputSpecificationSelection";
import RequestJsonViewerPopup from "./HowItWorks/RequestJsonViewerPopup";
import QuickStartSteps from "./HowItWorks/QuickStartSteps/index";
import {
  getTimePhasedDemoData,
  resetTimePhasedData,
} from "store/slices/DemoSlice";
import DemoRequest from "interfaces/howItWorks/DemoRequest";
import ExampleDataSelector from "./HowItWorks/ExampleDataSelector";
import { connect, useSelector } from "react-redux";
import { AppDispatch, RootState } from "store/store";
import StepDescription from "./HowItWorks/StepDescription";
import Loader from "components/common/controls/Loader";
import QuickStartHeader from "./HowItWorks/QuickStartHeader";
import QuickStartDescription from "./HowItWorks/QuickStartDescription";
import TryThisAtHome from "./HowItWorks/TryThisAtHome";
import { getRequestDemoData } from "store/slices/DemoSlice";
import "./HowItWorks/styles/HowItWorks.css";

interface Props {
  dispatch: AppDispatch;
  selectedProductId: number;
  demoExampleData: any[];
}

export function HowItWorks(props: Props) {
  const { dispatch, selectedProductId, demoExampleData } = props;
  const [selectedDemoExample, setSelectedDemoExample] = useState<any>(null);
  const [selectedDemoExampleRequestJson, setSelectedDemoExampleRequestJson] =
    useState<DemoRequest | null>(null);

  const [isOutputDataUpdated, setIsOutputDataUpdated] = useState(false);
  const [isRequestJsonViewerPopupVisible, setIsRequestJsonViewerPopupVisible] =
    useState(false);

  const timePhasedData = useSelector(
    (state: RootState) => state.demoData.timePhasedData
  );

  const isLoading = useSelector((state: RootState) => state.demoData.loading);

  const [outputSpecification, setOutputSpecification] =
    useState<OutputSpecification | null>(null);

  const getSelectedExampleRequestJson = (selectedDemoExample: any) => {
    let requestJson = JSON.parse(selectedDemoExample.requestJSON);
    return requestJson;
  };

  const getGroupOptions = () => {
    const activities =
      getSelectedExampleRequestJson(selectedDemoExample).Activities || [];
    if (activities.length) {
      const keys = Object.keys(activities[0]);
      return keys.map((key) => {
        return {
          display: key,
          value: key,
        };
      });
    }
    return [];
  };

  const handleSpecificationChanged = useCallback(
    (updatedSpecification: OutputSpecification) => {
      setOutputSpecification(updatedSpecification);
      setIsOutputDataUpdated(false);
    },
    []
  );

  const getDemoOutputRequestData = () => {
    const requestJson = getSelectedExampleRequestJson(selectedDemoExample);
    let requestData: DemoRequest = {
      DemoId: selectedDemoExample.id,
      FieldMappings: requestJson?.FieldMappings || {},
      Activities: requestJson?.Activities || [],
      Outputs: outputSpecification ? [outputSpecification] : [],
      Profiles: requestJson?.Profiles || [],
      calendars: requestJson?.calendars || [],
    };
    return requestData;
  };

  const getTimePhasedData = () => {
    const requestData = getDemoOutputRequestData();
    dispatch(
      getTimePhasedDemoData({
        demoRequest: requestData,
        outputKey: outputSpecification?.Title || "",
      })
    ).then(() => {
      setIsOutputDataUpdated(true);
    });
  };

  const handleViewRequestJsonClicked = () => {
    setIsRequestJsonViewerPopupVisible(true);
  };

  const handleExampleDataSelectionChange = useCallback((demoExample: any) => {
    setSelectedDemoExample(demoExample);
  }, []);

  useEffect(() => {
    if (demoExampleData.length === 0 && selectedProductId) {
      dispatch(getRequestDemoData(selectedProductId));
    }
  }, [demoExampleData.length, selectedProductId, dispatch]);

  useEffect(() => {
    if (demoExampleData.length) {
      setSelectedDemoExample(demoExampleData[0]);
    }
  }, [demoExampleData]);

  useEffect(() => {
    if (selectedDemoExample) {
      let requestJson = JSON.parse(selectedDemoExample.requestJSON);
      setOutputSpecification(requestJson.Outputs[0]);
      setSelectedDemoExampleRequestJson(requestJson);
      setIsOutputDataUpdated(false);
      dispatch(resetTimePhasedData());
    }
  }, [selectedDemoExample, dispatch]);

  return (
    <>
      <div className="ml-45 mt-175px">
        <QuickStartHeader />
      </div>
      <div className="mt-175px bg-promWhite1">
        <div className="py-20 ml-45">
          <QuickStartDescription />
        </div>
      </div>
      <div className="py-20 bg-white px-4">
        <QuickStartSteps />
      </div>
      <div className="space-y-6 px-4 bg-promWhite1 pt-20 pb-10">
        <div className="mb-16">
          <StepDescription />
        </div>
        <ExampleDataSelector
          demoExampleData={demoExampleData}
          selectedDemoExample={selectedDemoExample}
          handleChange={handleExampleDataSelectionChange}
        />
        {selectedDemoExample &&
          selectedDemoExampleRequestJson &&
          outputSpecification && (
            <>
              <ExampleDataViewer data={selectedDemoExampleRequestJson} />
              <OutputSpecificationSelection
                specification={outputSpecification}
                groupOptions={getGroupOptions()}
                onSpecificationChange={handleSpecificationChanged}
              />
              <div>
                <OutputDataRefresher
                  onRefreshDataClick={getTimePhasedData}
                  onViewRequestDataClick={handleViewRequestJsonClicked}
                  enabled={!isOutputDataUpdated}
                />
                {timePhasedData && <OutputDataViewer data={timePhasedData} />}
              </div>
            </>
          )}
      </div>
      <div className="pt-16 bg-promWhite1 px-4">
        <TryThisAtHome />
      </div>
      {isRequestJsonViewerPopupVisible && outputSpecification && (
        <RequestJsonViewerPopup
          onClose={() => setIsRequestJsonViewerPopupVisible(false)}
          json={getDemoOutputRequestData()}
        />
      )}
      <Loader isVisible={isLoading} />
    </>
  );
}

const mapStateToProps = (state: RootState, props: any) => {
  return {
    demoExampleData: state.demoData.demoExampleData,
    selectedProductId: state.productData.selectedProductId,
    ...props,
  };
};

export default connect(mapStateToProps)(HowItWorks);
